import React, { useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import EmployeeHeader from './components/EmployeeHeader';
import CompetencyDashboard from './components/CompetencyDashboard';
import FeedbackOnDemandSection from './components/FeedbackOnDemandSection';
import SkillsTable from './components/SkillsTable';
import { useFeedbackReport } from '../../hooks/useFeedbackReport';
import { useExpandableStates } from '../../hooks/useExpandableStates';
import {
  groupResponsesBySkill,
  calculateTemplateTypeStats,
  calculateOverallPerformance,
  getReviewerCompetencyLevels
} from '../../utils/feedbackReportUtils';

function FeedbackReportContent({ assignmentId, onDataLoaded }) {
  const { loading, reportData, error, sortedReviewers } = useFeedbackReport(assignmentId, onDataLoaded);
  const expandableStates = useExpandableStates();

  // Get reviewType with fallback for Individual Assignments
  const reviewType = useMemo(() => {
    // First try to get from feedbackSession
    if (reportData?.feedbackSession?.reviewType) {
      return reportData.feedbackSession.reviewType;
    }
    // Fallback: get from first reviewer (for Individual Assignments)
    if (reportData?.reviewers?.[0]?.reviewType) {
      return reportData.reviewers[0].reviewType;
    }
    // Default fallback
    return undefined;
  }, [reportData]);

  // Ensure reportData has reviewType in feedbackSession for calculateTemplateTypeStats
  const enrichedReportData = useMemo(() => {
    if (!reportData) return null;

    // If reviewType is missing from feedbackSession but we found it elsewhere, add it
    if (!reportData.feedbackSession?.reviewType && reviewType) {
      return {
        ...reportData,
        feedbackSession: {
          ...reportData.feedbackSession,
          reviewType
        }
      };
    }

    return reportData;
  }, [reportData, reviewType]);

  // Memoized data processing
  const skillsMap = useMemo(() => groupResponsesBySkill(enrichedReportData), [enrichedReportData]);

  const templateTypeStats = useMemo(() => calculateTemplateTypeStats(enrichedReportData), [enrichedReportData]);

  const overallPerformance = useMemo(() => calculateOverallPerformance(templateTypeStats), [templateTypeStats]);

  const skillLevels = useMemo(() => {
    return enrichedReportData?.reviewers?.[0] ? getReviewerCompetencyLevels(enrichedReportData.reviewers[0]) : [];
  }, [enrichedReportData]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <EmptyState title="Error Loading Report" description={error} />;
  }

  if (!enrichedReportData) {
    return (
      <EmptyState
        title="No Data Available"
        description="No feedback report data is available for this assignment."
      />
    );
  }

  return (
    <>
      {/* Main Header Section */}
      <Box sx={{ p: 2, mb: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          width="100%"
        >
          <EmployeeHeader reportData={enrichedReportData} />
          <CompetencyDashboard
            templateTypeStats={templateTypeStats}
            overallPerformance={overallPerformance}
            skillLevels={skillLevels}
            reviewType={reviewType}
          />
        </Stack>
      </Box>

      {/* Feedback on Demand Section */}
      <FeedbackOnDemandSection feedbackOnDemand={enrichedReportData?.feedbackOnDemand} />

      {/* Skills Table */}
      <SkillsTable
        skillsMap={skillsMap}
        sortedReviewers={sortedReviewers}
        expandableStates={expandableStates}
        reviewType={reviewType}
      />
    </>
  );
}

export default FeedbackReportContent; 